import { useLanguage } from '@spazious/dollhouse-i18n';
import { useStore } from '@spazious/dollhouse-store';
import { UnitType } from '@spazious/dollhouse-types';
import { feetToMeter, meterToFeet, meterToMetric } from '@spazious/dollhouse-utils';

export interface UnitConversionProps {
  value: number | string;
  conversion?: {
    enabled: boolean;
    type?: string;
  };
  format?: {
    formatValue: boolean;
    fractionDigits: number;
  };
}
export interface DisplayUnitProps {
  value: string;
  unit: string;
  type: string;
}

export interface FormatNumberProps {
  value: number | string;
  fractionDigits: number;
}

export const useUnitConverter = () => {
  const { measurementsUnit } = useStore();
  const { locale } = useLanguage();

  const formatWithUnit = (value: number, unit: string, fractionDigits: number) => {
    const formatter = new Intl.NumberFormat(locale, {
      style: 'unit',
      unit: unit,
      maximumFractionDigits: fractionDigits,
    });

    return formatter.format(value);
  };
  const format = (result: number, fractionDigits: number) => {
    const formatter = new Intl.NumberFormat(locale, {
      maximumFractionDigits: fractionDigits,
    });
    return formatter.format(result);
  };

  const convertToEnglishLocale = (formattedNumber: string): number => {
    const numberValue = Number(formattedNumber.replace(',', '.'));
    const formatter = new Intl.NumberFormat('en');
    return parseFloat(formatter.format(numberValue));
  };

  const displayUnit = (
    value: number | string,
    showUnit: boolean = false,
    customMeasurementUnit: string = measurementsUnit,
    fractionDigits: number = 2
  ) => {
    const numericValue = parseFloat(value as string);
    const result =
      measurementsUnit === UnitType.Feet
        ? meterToFeet(numericValue)
        : meterToMetric(numericValue, customMeasurementUnit);

    return showUnit
      ? formatWithUnit(result, customMeasurementUnit, fractionDigits)
      : format(result, fractionDigits);
  };

  const standardUnit = (
    value: string,
    customMeasurementUnit: string = measurementsUnit,
    fractionDigits: number = 2
  ) => {
    const numericValue = locale !== 'en' ? convertToEnglishLocale(value) : parseFloat(value);

    const result =
      measurementsUnit === UnitType.Feet
        ? feetToMeter(numericValue)
        : meterToMetric(numericValue, customMeasurementUnit);

    return result.toFixed(fractionDigits);
  };

  return {
    displayUnit,
    standardUnit,
  };
};
